import {
  Button,
  Grid,
  makeStyles,
  MenuItem,
  TextField,
} from "@material-ui/core";
import { ICompanyData } from "components/interfaces";
import CustomTextField from "components/UI/Fields/CustomHelperTextField";
import { PageTitle } from "components/UI/Page";
import useValidations from "hooks/useValidations";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { lang } from "../../i18nLang";
import { IntegrationSetupStore as Store } from "../../store";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "30vw",
    margin: "1.5rem auto",
    padding: theme.spacing(6, 2),
  },
  title: {
    textTransform: "uppercase",
    fontWeight: 700,
  },
  content: {
    minHeight: "calc(60vh - 116px)",
    padding: theme.spacing(4, 12),
    paddingTop: 0,
  },
  actions: { justifyContent: "center" },
  formPage: { backgroundColor: "#E5E5E5", width: "100%" },
  button_wrapper: {
    margin: 0,
    padding: 0,
    flex: 0,
    flexBasis: "25%",
  },
}));

const softwares = [{ key: 1, value: "Mega" }];
const linkIntegration =
  "https://megasistemas.atlassian.net/wiki/spaces/RF/pages/2644672516/Como+fazer+a+integra+o+junto+ao+Mega+ERP";

interface IProps {
  data: ICompanyData;
  update?: boolean;
  disabled?: boolean;
  enableSave?: boolean;
  onInputChange: Function;
  onSave: Function;
}

let timeoutId: any = undefined;

/** Aréa da configuração da integração.
 * @category Components
 */
const IntegrationSetup = observer(
  ({
    data,
    update = false,
    disabled = false,
    enableSave = false,
    onInputChange,
    onSave,
  }: IProps) => {
    const classes = useStyles();
    // Utilizada para evitar o autocomplete no chrome
    const [disablePass, setDisablePass] = useState(true);
    const { validated, validateFields, reset } = useValidations(
      Store.validations
    );
    const [errors, setErrors] = useState<any>(undefined);

    useEffect(() => {
      reset();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Utilizada para evitar o autocomplete no chrome
    useEffect(() => {
      setDisablePass(true);
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(
        () => setDisablePass(false),
        !data.password ? 2000 : 200
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [update]);

    const handleConfirm = async () => {
      if (
        !validateFields(
          {
            host: data,
            user: data,
            password: data,
          },
          true
        )
      ) {
        return;
      }

      onSave().finally(() => setErrors(Store.validateAuthError(data)));
    };

    const handleBlur = (ev) => {
      const { name, value } = ev.target;
      const dateToValidate = {
        host: value,
        user: value,
        password: value,
      };
      validateFields({ [name]: dateToValidate });
      setErrors(Store.validateAuthError(dateToValidate));
    };

    const handleChangeInput = (ev) => {
      if (!disablePass) onInputChange(ev);
    };

    const setCustomValidatedData = (key) => {
      if (validated[key]?.valid)
        if (errors && (errors.fields || []).includes(key)) {
          return {
            isLink: errors.isLink,
            valid: false,
            text:
              key === "password"
                ? ""
                : lang(`integrationSetup.error.${errors.type}`),
          };
        }
      return {
        text: validated[key]?.text || "",
        valid: validated[key]?.valid || true,
        isLink: false,
      };
    };

    const customValidated = {
      host: setCustomValidatedData("host"),
      user: setCustomValidatedData("user"),
      password: setCustomValidatedData("password"),
      tenantId: setCustomValidatedData("tenantId"),
    };

    return (
      <>
        <Grid container>
          <PageTitle className={classes.title}>
            {lang("integrationSetup.title")}
          </PageTitle>
          <TextField
            name="software"
            id="select-sofware"
            select
            label={lang("integrationSetup.software")}
            fullWidth
            value={data.software}
            disabled={disabled}
            margin="normal"
            onChange={handleChangeInput}
            onKeyDown={(ev) => ev.stopPropagation()}
          >
            {softwares.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.value}
              </MenuItem>
            ))}
          </TextField>
          <CustomTextField
            name="host"
            label={lang("integrationSetup.host")}
            margin="normal"
            value={data.host}
            fullWidth
            disabled={disabled}
            isLink={customValidated.host.isLink}
            link={{
              href: linkIntegration,
              message: lang("integrationSetup.seeMoreConfig"),
            }}
            onBlur={handleBlur}
            onChange={handleChangeInput}
            onKeyDown={(ev) => ev.stopPropagation()}
            helperText={customValidated.host.text}
            error={!customValidated.host.valid}
          />
          <TextField
            name="tenantId"
            label={lang("integrationSetup.tenantId")}
            value={data.tenantId}
            autoComplete="off"
            margin="normal"
            fullWidth
            type="text"
            onBlur={handleBlur}
            onChange={handleChangeInput}
            onKeyDown={(ev) => ev.stopPropagation()}
            disabled={disablePass || disabled}
            helperText={customValidated.tenantId.text}
            error={!customValidated.tenantId.valid}
          />
          <CustomTextField
            name="user"
            label={lang("integrationSetup.user")}
            value={data.user}
            autoComplete="off"
            margin="normal"
            fullWidth
            disabled={disabled}
            isLink={customValidated.user.isLink}
            link={{
              href: linkIntegration,
              message: lang("integrationSetup.seeMore"),
            }}
            onBlur={handleBlur}
            onChange={handleChangeInput}
            onKeyDown={(ev) => ev.stopPropagation()}
            helperText={customValidated.user.text}
            error={!customValidated.user.valid}
          />
          <TextField
            name="password"
            label={lang("integrationSetup.password")}
            value={data.password}
            autoComplete="off"
            margin="normal"
            fullWidth
            type="password"
            onBlur={handleBlur}
            onChange={handleChangeInput}
            onKeyDown={(ev) => ev.stopPropagation()}
            disabled={disablePass || disabled}
            helperText={customValidated.password.text}
            error={!customValidated.password.valid}
          />
        </Grid>
        <Grid container item justifyContent="flex-end">
          <Button
            color="primary"
            variant="contained"
            disabled={disabled || !enableSave}
            onClick={handleConfirm}
            style={{ height: "fit-content" }}
          >
            {lang("save")}
          </Button>
        </Grid>
      </>
    );
  }
);

export default IntegrationSetup;
